<template>
  <div>
    <modal-template ref="modal" modal-size="2xl">
      <template v-slot:title>
        {{ lang.modals.add_credit_card.headline }}
      </template>

      <template v-slot:default>
        <div class="text-left">
          <div class="grid grid-cols-2">
            <div class="col-span-2 sm:col-span-1">
              <!-- Card owner -->
              <input-text
                :label="lang.labels.card_owner"
                :required="true"
                id="cardHolderName"
                name="cardHolderName"
                v-model="cardHolderName"
                :error="formErrors.has('cardHolderName')"
              ></input-text>
            </div>
          </div>
          <div class="col-span-2 sm:col-span-1"></div>
          <div class="col-span-2 space-y-4">
            <div class="mt-4">
              <label for="card-element">
                {{ lang.labels.card_number }} *
              </label>
              <div id="card-element">
                <!-- a Stripe Element will be inserted here. -->
              </div>
            </div>

            <!-- Used to display form errors -->
            <div
              class="alert alert-danger mt20"
              role="alert"
              v-show="showErrorMsg"
            >
              {{ errorMsg }}
            </div>

            <!-- Accept T&C -->
            <div
              class="form-group"
              :class="{
                'has-error': formErrors.has('tAndC'),
                errorShake: formErrors.has('tAndC') && shakeOnError,
              }"
            >
              <div class="checkbox">
                <input-checkbox
                  name="t_and_c"
                  id="t_and_c"
                  v-model="tAndC"
                  :required="true"
                >
                  <span
                    v-html="lang.msg.read_and_accept_tnc_for_creditCard"
                  ></span>
                </input-checkbox>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:footer="footerSlotProps">
        <div
          class="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:space-x-4"
        >
          <plain-button color="default" @click="hideModal()">{{
            lang.labels.cancel
          }}</plain-button>

          <plain-button color="primary" @click="storeCreditCard()">{{
            lang.labels.save
          }}</plain-button>
        </div>
      </template>
    </modal-template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import FormErrors from "./../../FormErrors.js";
import ModalTemplate from "./ModalTemplate.vue";
import InputText from "../inputs/InputText.vue";
import InputCheckbox from "../inputs/InputCheckbox.vue";

// Props
const props = defineProps({
  lang: Object,
  modalData: Object,
});

// Template refs
const modal = ref(null);

// Reactive state
const cardHolderName = ref("");
const tAndC = ref(false);
const stripe = ref("");
const stripeKey = ref("");
const stripeSetupIntentClientSecret = ref("");
const card = ref("");
const disableButtons = ref(false);
const shakeOnError = ref(true);
const formErrors = ref(new FormErrors());
const errorMsg = ref("");
const initSetUpDone = ref(false); // check if init function has run already

// Computed
/**
 * define if error msg should be displayed
 * @return: bool
 */
const showErrorMsg = computed(() => {
  return errorMsg.value !== "";
});

// Methods
/**
 * initialize function when modal is being loaded
 */
const init = () => {
  if (!initSetUpDone.value) {
    createSetupIntent();
    initSetUpDone.value = true;
  }
};

/**
 * create a stripe setupIntent and receive the client_secret
 */
const createSetupIntent = () => {
  axios
    .get(`/web-api/stripe/setup-intent`)
    .then((response) => {
      stripeKey.value = response.data.stripeKey;
      stripeSetupIntentClientSecret.value =
        response.data.stripeSetupIntentClientSecret;
      renderCreditCardForm();
    })
    .catch((error) => {
      console.log(error);
    });
};

/**
 * render credit card payment form
 */
const renderCreditCardForm = () => {
  // Create a Stripe client.
  stripe.value = window.Stripe(stripeKey.value);

  // Create an instance of Elements.
  let elements = stripe.value.elements({
    locale: "de",
  });

  // Custom styling can be passed to options when creating an Element.
  let style = {
    base: {
      color: "#32325d",
      fontFamily: 'Lato, "Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "14px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  };

  // Create an instance of the card Element.
  card.value = elements.create("card", { style: style });

  // Add an instance of the card Element into the `card-element` <div>.
  card.value.mount("#card-element");

  // Handle real-time validation errors from the card Element.
  card.value.addEventListener("change", (event) => {
    let displayError = document.getElementById("card-errors");
    if (event.error) {
      errorMsg.value = event.error.message;
    } else {
      errorMsg.value = "";
    }
  });
};

/**
 * Validate - check mandatory fields
 */
const validateForm = () => {
  formErrors.value.clear();
  shakeOnError.value = true;
  let errors = {};

  // cardHolderName
  if (cardHolderName.value == "" || cardHolderName.value == null)
    errors.cardHolderName = "missing";

  if (!tAndC.value) errors.tAndC = "missing";

  formErrors.value.record(errors);

  if (formErrors.value.any()) {
    setTimeout(() => {
      shakeOnError.value = false;
    }, 500);

    return false;
  }

  return true;
};

/**
 * store new CreditCard
 */
const storeCreditCard = () => {
  disableButtons.value = true;

  let validation = validateForm();

  if (validation) {
    sendCardDetails();
  } else {
    setTimeout(() => {
      disableButtons.value = false;
    }, 500);
  }
};

/**
 * send and store card details to stripe
 */
const sendCardDetails = () => {
  stripe.value
    .handleCardSetup(stripeSetupIntentClientSecret.value, card.value, {
      payment_method_data: {
        billing_details: {
          name: cardHolderName.value,
        },
      },
    })
    .then((result) => {
      if (result.error) {
        errorMsg.value = result.error.message;
        disableButtons.value = false;
      } else {
        // send setupIntent and payment method data to server to attach payment method to stripe customer
        axios
          .post(`/web-api/stripe/add-credit-card`, {
            setupIntent: result.setupIntent,
          })
          .then((response) => {
            // console.log(response);
            location.reload();
          })
          .catch((error) => {
            // console.log(error)
          });
      }
    });
};

const showModal = () => {
  modal.value.showModal();
  init();
  // writeLogs();
};

const hideModal = () => {
  modal.value.hideModal();
};

// Expose methods for parent components
defineExpose({
  showModal,
  hideModal,
});
</script>
